/**
 * @category Context
 * @module TutorialContext
 * @description Context for managing tutorial state and progression throughout the application
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { WebSocketContext } from "./WebSocketContext";
import { AuthContext } from "./AuthContext";
import {
  loadTutorial,
  evaluateStepPredicates,
  findNextStep,
  evaluatePredicate,
} from "../components/leftSide/Tutorial/utils/tutorialLoader";
import {
  getCurrentTutorialContent,
  getCurrentTutorialTitle,
  getCurrentTutorialHelp,
  //createCompatibilityContent,
} from "../components/leftSide/Tutorial/utils/tutorialContentRenderer";
import { hasProperty } from "../utils/helpers";
//import { overEvery } from "lodash";
const TutorialContext = createContext();

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error("useTutorial must be used within a TutorialProvider");
  }
  return context;
};

// Helper hook for components that only need to check and update tutorial progress
export const useTutorialProgress = () => {
  const {
    isTutorialActive,
    monitorStateChange,
    checkApplicationState,
  } = useTutorial();

  return {
    isTutorialActive,
    monitorStateChange,
    checkApplicationState,
  };
};

export const TutorialProvider = ({ children }) => {
  const [localTutorialModalOpen, setLocalTutorialModalOpen] = useState(false);
  const [currentTutorial, setCurrentTutorial] = useState(null); // null, "tutorial1", etc.
  const [runningTutorial, setRunningTutorialRaw] = useState(0);
  const runningTutorialRef = useRef(0); // Keep a ref for immediate access

  // NEW: JSON-based tutorial system state
  const [loadedTutorialDefinition, setLoadedTutorialDefinition] =
    useState(null);

  // WebSocket context for sending tutorial monitoring messages
  const { sendJsonMessage } = useContext(WebSocketContext);
  
  // Auth context for getting current user ID
  const { currentUser } = useContext(AuthContext);

  // Wrap setRunningTutorial to add debugging and sync ref
  const setRunningTutorial = (value) => {
    runningTutorialRef.current = value; // Update ref immediately
    setRunningTutorialRaw(value);
  };
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialWelcomeModalOpen, setTutorialWelcomeModalOpen] =
    useState(false);

  // Load tutorial state from sessionStorage on mount
  useEffect(() => {
    try {
      const savedTutorialState = sessionStorage.getItem("tutorialState");
      if (savedTutorialState) {
        const state = JSON.parse(savedTutorialState);
        setCurrentTutorial(state.currentTutorial || null);
        setLocalTutorialModalOpen(state.localTutorialModalOpen || false);
        const tutorialStep = state.runningTutorial || 0;
        runningTutorialRef.current = tutorialStep; // Update ref immediately
        setRunningTutorialRaw(tutorialStep);
        setTutorialOpen(state.tutorialOpen || false);
        setTutorialWelcomeModalOpen(state.tutorialWelcomeModalOpen || false);
      }
    } catch (error) {
      console.warn("Failed to load tutorial state from sessionStorage:", error);
    }
  }, []);

  // Save tutorial state to sessionStorage whenever it changes
  useEffect(() => {
    try {
      const tutorialState = {
        currentTutorial,
        localTutorialModalOpen,
        runningTutorial,
        tutorialOpen,
        tutorialWelcomeModalOpen,
      };
      sessionStorage.setItem("tutorialState", JSON.stringify(tutorialState));
    } catch (error) {
      console.warn("Failed to save tutorial state to sessionStorage:", error);
    }
  }, [
    currentTutorial,
    localTutorialModalOpen,
    runningTutorial,
    tutorialOpen,
    tutorialWelcomeModalOpen,
  ]);

  // Check for tutorial completion when runningTutorial changes
  useEffect(() => {
    // Check if tutorial exists and is active
    if (currentTutorial && runningTutorial && runningTutorial > 0 && loadedTutorialDefinition) {
      const currentStep = loadedTutorialDefinition.stepMap[runningTutorial];
       // Check if current step exists and doesn't have a nextStep property
      if (currentStep && (!hasProperty(currentStep, "nextStep") || currentStep.nextStep === null || (hasProperty(currentStep, "isComplete") && currentStep.isComplete === true))) {
        // Tutorial is completed - send completion message to backend
        try {
          console.log("sending tutorial completion message to backend");
          sendJsonMessage({
            User: currentUser.id,
            Monitoring: {
              Tutorial: [{
                name: currentTutorial,
                completed: true
              }]
            }
          }, false);
        } catch (error) {
          console.warn("Failed to send tutorial completion message:", error);
        }
      }
    }
  }, [runningTutorial]);

  const startTutorial = (tutorial) => {
    setCurrentTutorial(tutorial);
    setRunningTutorial(1);
    setTutorialOpen(true);
    setLocalTutorialModalOpen(true);
    setTutorialWelcomeModalOpen(false);

    // Send tutorial monitoring message to backend
    try {
      sendJsonMessage({
        User: currentUser.id,
        Monitoring: {
          Tutorial: [{
            name: tutorial,
            completed: false
          }]
        }
      }, false);
    } catch (error) {
      console.warn("Failed to send tutorial monitoring message:", error);
    }

    // NEW: Load JSON tutorial definition if using new system

    try {
      const tutorialDef = loadTutorial(tutorial);
      if (tutorialDef) {
        setLoadedTutorialDefinition(tutorialDef);
      }
    } catch (error) {
      console.error(`❌ Error loading JSON tutorial '${tutorial}':`, error);
    }
  };

  // NEW: Check initial state and skip ahead if conditions are met

  const closeTutorialModal = () => {
    setRunningTutorial(0);
    setLocalTutorialModalOpen(false);
    setTutorialWelcomeModalOpen(false);
    setTutorialOpen(false);
  };

  const checkAndUpdateTutorial = (input) => {
    // Use ref to get the most current tutorial step (bypasses async state updates)
    const currentTutorialStep = runningTutorialRef.current;
 
    if (!localTutorialModalOpen || !currentTutorial) {
      return false;
    }

    // NEW: Try JSON-based system first
    if (loadedTutorialDefinition && currentTutorialStep) {
      return checkJsonBasedTutorial(input, currentTutorialStep);
    }

    // LEGACY: Original hardcoded system (kept for backwards compatibility)
    return false;
  };

  // NEW: Enhanced JSON-based tutorial checking with predicates
  const checkJsonBasedTutorial = (input, currentTutorialStep) => {
    const currentStep = loadedTutorialDefinition.stepMap[currentTutorialStep];
    if (!currentStep) {
      return false;
    }

    // Enhanced predicate evaluation with context
    const context = {
      currentStep: currentTutorialStep,
      tutorial: loadedTutorialDefinition,
      input: input,
      // Add more context as needed (user data, app state, etc.)
    };

    // Use enhanced predicate evaluation system
    const isMatch = evaluateStepPredicates(
      currentStep.predicates,
      input,
      context
    );

    if (isMatch) {
      // Use enhanced step finder to determine next step
      const nextStep = findNextStep(currentStep, input, context);
      if (nextStep) {
        setRunningTutorial(nextStep);
      } 
      return true;
    }

    return false;
  };

  // NEW: State-based tutorial progression detection
  const checkStateBasedTutorial = (stateChange) => {
    if (
      !loadedTutorialDefinition ||
      !localTutorialModalOpen ||
      !currentTutorial
    ) {
      return false;
    }

    const currentStepNumber = runningTutorialRef.current; // Use ref for immediate access
    const currentStep = loadedTutorialDefinition.stepMap[currentStepNumber];
    if (!currentStep) return false;

    // Check if this state change matches any predicates for the current step
    const context = {
      currentStep: currentStepNumber,
      tutorial: loadedTutorialDefinition,
      stateChange: stateChange,
    };

    // Look for state-based predicates
    const statePredicates = currentStep.predicates;

    for (const predicate of statePredicates) {
      if (evaluatePredicate(predicate, stateChange.type, context)) {
    
        const nextStep = findNextStep(currentStep, stateChange.type, context);
        if (nextStep && nextStep !== null) {
          setRunningTutorial(nextStep);
          return true;
        }
      }
    }

    return false;
  };

  const resetTutorial = () => {
    setCurrentTutorial(null);
    setRunningTutorial(0);
    setLocalTutorialModalOpen(false);
    setTutorialWelcomeModalOpen(false);
    setTutorialOpen(false);
    setLoadedTutorialDefinition(null); // Clear JSON tutorial data

    try {
      // Save default/reset state instead of removing entirely
      const defaultTutorialState = {
        currentTutorial: null,
        localTutorialModalOpen: false,
        runningTutorial: 0,
        tutorialOpen: false,
        tutorialWelcomeModalOpen: false,
      };
      sessionStorage.setItem(
        "tutorialState",
        JSON.stringify(defaultTutorialState)
      );
    } catch (error) {
      console.warn("Failed to reset tutorial state in sessionStorage:", error);
    }
  };

  const isTutorialActive = () => {
    return localTutorialModalOpen && runningTutorial > 0;
  };

  const getTutorialStep = () => {
    return runningTutorial;
  };

  // NEW: Monitor app state changes for tutorial progression
  const monitorStateChange = (changeType, data = {}) => {
    if (!currentTutorial) return false;

    const stateChange = {
      type: changeType,
      data: data,
      timestamp: new Date().toISOString(),
    };

    return checkStateBasedTutorial(stateChange);
  };

  // NEW: Check application state for tutorial conditions
  const checkApplicationState = (appState = {}) => {
    if (!currentTutorial || !loadedTutorialDefinition || !runningTutorialRef.current)
      return false;

    const currentStepNumber = runningTutorialRef.current; // Use ref for immediate access
    const currentStep = loadedTutorialDefinition.stepMap[currentStepNumber];
    if (!currentStep || !hasProperty(currentStep, "skipConditions")){
      return false;
    }
    const skipConditions = currentStep.skipConditions;
    if (skipConditions) {
      for (const skipCondition of skipConditions) {
        let toNextStep = evaluatePredicate(skipCondition, "skipConditions", {
          stateChange: { type: "skipConditions", data: appState },
        });
        if (toNextStep) {
          let nextStep = currentStepNumber + 1;
          if (hasProperty(skipCondition, "nextStep")) {
            nextStep = skipCondition.nextStep;
          }
          setRunningTutorial(nextStep);
          return true;
        }
      }
    }
    return false;
  };

  // NEW: Get current tutorial definition (JSON format)
  const getCurrentTutorialDefinition = () => {
    return loadedTutorialDefinition;
  };

  // NEW: Get current step definition (JSON format)
  const getCurrentStepDefinition = (otherStep = null) => {
    if (!loadedTutorialDefinition || !runningTutorial) return null;
    if (otherStep !== null && otherStep < runningTutorial) {
      return loadedTutorialDefinition.stepMap[otherStep];
    }
    return loadedTutorialDefinition.stepMap[runningTutorial];
  };

  // NEW: Get current tutorial content (rendered JSX)
  const getCurrentTutorialContentJSX = (otherStep = null) => {
    if (!loadedTutorialDefinition) return null;
    if (otherStep !== null && otherStep < runningTutorial) {
      return getCurrentTutorialContent(loadedTutorialDefinition, otherStep);
    }
    return getCurrentTutorialContent(loadedTutorialDefinition, runningTutorial);
  };

  // NEW: Get current tutorial title
  const getCurrentTutorialTitleText = (otherStep = null) => {
    if (!loadedTutorialDefinition) return null;
    if (otherStep !== null && otherStep < runningTutorial) {
      return getCurrentTutorialTitle(loadedTutorialDefinition, otherStep);
    }
    return getCurrentTutorialTitle(loadedTutorialDefinition, runningTutorial);
  };

  // NEW: Get current tutorial help content
  const getCurrentTutorialHelpText = (otherStep = null) => {
    if (!loadedTutorialDefinition) return null;
    if (otherStep !== null && otherStep < runningTutorial) {
      return getCurrentTutorialHelp(loadedTutorialDefinition, otherStep);
    }
    return getCurrentTutorialHelp(loadedTutorialDefinition, runningTutorial);
  };

  // NEW: Get compatibility content (for components that still use old format)
  // const getCompatibilityContent = (otherStep = null) => {
  //   if (!loadedTutorialDefinition) return null;
  //   if (otherStep !== null && otherStep < runningTutorial) {
  //     return createCompatibilityContent(loadedTutorialDefinition, otherStep);
  //   }
  //   return createCompatibilityContent(loadedTutorialDefinition);
  // };

  const value = {
    // State
    currentTutorial,
    localTutorialModalOpen,
    runningTutorial,
    tutorialOpen,
    tutorialWelcomeModalOpen,

    // Actions
    startTutorial,
    closeTutorialModal,
    checkAndUpdateTutorial,
    resetTutorial,

    // Utilities
    isTutorialActive,
    getTutorialStep,

    // NEW: JSON-based tutorial functionality
    getCurrentTutorialDefinition,
    getCurrentStepDefinition,
    loadedTutorialDefinition,

    // NEW: Step navigation functions
    // goToNextStep,
    // goToPreviousStep,
    // goToStep,

    // NEW: State monitoring
    monitorStateChange,
    checkApplicationState,

    // NEW: Content rendering functions
    getCurrentTutorialContentJSX,
    getCurrentTutorialTitleText,
    getCurrentTutorialHelpText,
    //getCompatibilityContent,

    // Direct state setters (for backward compatibility if needed)
    setCurrentTutorial,
    setTutorialOpen,
    setTutorialWelcomeModalOpen,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

export default TutorialContext;
